// Sub-case shapes returned by `GET /v1/cases/:caseUrn/sub-cases` on the
// operator-bff. Mirrors the apps/cases canonical SubCase + the airline
// manifest fan-out: one sub-case per PNR, with passenger entries fetched
// from the airline ACL at case-open time.

export type PaxType = 'ADT' | 'CHD' | 'INF';

export interface PassengerEntry {
  paxUrn: string;
  firstName: string;
  lastName: string;
  paxType: PaxType;
  loyaltyTier?: string;
}

export type SubCaseStatus =
  | 'PENDING'
  | 'OPTIONS_OFFERED'
  | 'AWAITING_OPERATOR'
  | 'OPERATOR_REVIEWING'
  | 'COMPENSATION_PROPOSED'
  | 'COMPENSATION_CONFIRMED'
  | 'COMPENSATION_REJECTED'
  | 'COMPLETED'
  | 'FAILED';

export interface SubCase {
  urn: string;
  caseUrn: string;
  pnrUrn: string;
  passengers: PassengerEntry[];
  status: SubCaseStatus;
  lockedByOperatorUrn?: string;
  lockedByDisplayName?: string;
  version: number;
  // Rebooking outcome denormalized onto the sub-case (Lane B). Drives whether a
  // group still needs an overnight hotel: a same-day reflight (requiresHotel
  // false) skips the hotel board. Absent until Lane B ships it → treat as
  // "needs hotel" so nothing is hidden prematurely.
  rebooking?: {
    status?: 'PENDING' | 'OPTIONS_READY' | 'SELECTED' | 'OVERRIDDEN' | 'NO_OPTIONS' | 'NOT_APPLICABLE';
    requiresHotel?: boolean;
    /** WHY `requiresHotel` holds its value (rebooking §12). `OVERRIDDEN` with
     *  `requiresHotel === false` = an explicit suppression (stay-home). */
    hotelRequirementReason?: string;
    /** `LOCAL_RESIDENT` = operator-declared "stays at own home". */
    passengerElection?: string;
    /** Why the engine produced no options; `POLICY_DISABLED` is what reclassifies a
     *  group to `NOT_APPLICABLE` (engine off for the station). Pass-through from cases. */
    noOptionsReason?: string;
    /** Ride-home destination (non-PII members) when the declaration carried an
     *  address — mirrors the roster row's `homeDestination`. */
    homeDestination?: import('@/lib/disruptions/types').HomeDestination;
    /** Who chose the SELECTED flight: Nexa's engine or the airline's own
     *  reaccommodation (`rebooking.selected {source}`, flight-delay S7). Pass-through. */
    source?: 'NEXA' | 'AIRLINE_EXTERNAL';
    /** Departure of the assigned flight (ISO), present once SELECTED. Pass-through. */
    assignedDeparture?: string;
  };
  /** The hotel the group holds NOW (`booking.held|succeeded` denorm) — absent when none.
   *  `HELD` = the reversible offer awaits the passenger; `CONFIRMED` = booked. Pass-through. */
  compensationData?: {
    reservationUrn?: string;
    reservationStatus?: 'HELD' | 'CONFIRMED';
  };
  /** Declared outcome WITHOUT a hotel (hotel desk / the passenger's «me voy a mi casa»).
   *  Absent = no declaration. Pass-through from cases. */
  accommodationOutcome?: {
    outcome: 'SELF_MANAGED' | 'NO_ACCOMMODATION' | 'AIRPORT_WAIT';
  };
}

/**
 * Whether a group still needs a hotel allocation after rebooking — the case
 * allocation tab and `components/allocation/scope.ts`. One of the UI copies of the
 * domain's §12 gate (`cases:needsHotelAllocation@v2` / `booking:needsHotelAllocation@v2`,
 * flight-delay S4); `routes/disruptions/hotel-desk/desk-model.hotel-rule.test.ts` runs
 * the domain's own decision table against it and pins every divergence by name.
 *
 *   • EXPLICIT SUPPRESSION      → false (requiresHotel === false AND
 *     hotelRequirementReason === 'OVERRIDDEN' — the stay-home declaration —
 *     wins at ANY status, before the status switch below). Same as the domain.
 *   • no rebooking field        → true  (legacy / engine never ran). Same as the domain.
 *   • NO_OPTIONS                → true  (couldn't reflight → needs overnight). Same.
 *   • SELECTED / OVERRIDDEN     → requiresHotel === true (overnight-then-fly only). Same.
 *   • NOT_APPLICABLE            → the stamp's own verdict (flight-delay U1, v2):
 *       · requiresHotel === false → no hotel — the daytime flight-delay stamp (the
 *         passenger keeps the flight) and every explicit "no" — as the domain;
 *       · requiresHotel === true  → hotel — the overnight delay stamp and the
 *         care-only stamp — as the domain;
 *       · requiresHotel ABSENT    → hotel — the `POLICY_DISABLED` fan-out stamp and
 *         legacy rows — as the domain. "Went home" is no longer an absent row: stay-home
 *         writes the explicit suppression (`false` + `OVERRIDDEN`) handled above.
 *   • PENDING / OPTIONS_READY / unknown → true. DELIBERATE divergence, kept: the
 *     domain says "not yet eligible", but the operator may allocate while an ordinary
 *     cancellation is still rebooking (the BFF plan keeps the same candidates — B1).
 *     A delay case never takes this path: S1 stamps every group NOT_APPLICABLE.
 */
export function needsHotelAllocation(subCase: SubCase): boolean {
  const rb = subCase.rebooking;
  // Explicit suppression wins at any status: the operator declared no hotel
  // demand (stay-home) — never re-derive one from the rebooking lifecycle.
  if (rb && rb.requiresHotel === false && rb.hotelRequirementReason === 'OVERRIDDEN') return false;
  if (!rb || !rb.status) return true;
  switch (rb.status) {
    case 'NO_OPTIONS':
      return true;
    case 'SELECTED':
    case 'OVERRIDDEN':
      return rb.requiresHotel === true;
    case 'NOT_APPLICABLE':
      // Only an explicit `false` withholds the room; true or absent → hotel (domain v2).
      return rb.requiresHotel !== false;
    case 'PENDING':
    case 'OPTIONS_READY':
    default:
      return true;
  }
}
